import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { SITE_URL, DEFAULT_OG_IMAGE } from '../config/business';

// Schema.org Organization — inclus pe TOATE paginile pentru semnal SEO local + cunoastere brand.
// Datele juridice sunt cele oficiale Stanley Software SRL.
const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Private Brands',
  legalName: 'Stanley Software SRL',
  url: SITE_URL,
  logo: DEFAULT_OG_IMAGE,
  email: 'contact@privatebrands.ro',
  description: 'Romania AI Brand Index — platforma de monitorizare a vizibilitatii brandurilor in motoarele AI generative (ChatGPT, Gemini, Claude, Perplexity).',
  foundingDate: '2024',
  taxID: 'RO51059100',
  vatID: 'RO51059100',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Intrarea Nicolae Iorga nr. 74 C',
    addressLocality: 'Chiajna',
    addressRegion: 'Ilfov',
    addressCountry: 'RO',
  },
  identifier: [
    { '@type': 'PropertyValue', propertyID: 'CUI', value: 'RO51059100' },
    { '@type': 'PropertyValue', propertyID: 'Reg. Com.', value: 'J2024050278004' },
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'contact@privatebrands.ro',
    areaServed: 'RO',
    availableLanguage: ['Romanian', 'English'],
  },
};

// Schema WebSite cu SearchAction pentru sitelinks searchbox in Google
const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Private Brands',
  url: SITE_URL,
  inLanguage: 'ro-RO',
  publisher: { '@type': 'Organization', name: 'Private Brands' },
  potentialAction: {
    '@type': 'SearchAction',
    target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/branduri?q={search_term_string}` },
    'query-input': 'required name=search_term_string',
  },
};

/**
 * Component reutilizabil pentru meta tags SEO + Open Graph + Twitter Card.
 *
 * Props:
 *  - title: titlul paginii (afisat in tab-ul browserului si rezultatele Google)
 *  - description: meta description (max ~160 caractere pentru Google)
 *  - path: calea relativa a paginii (ex. "/login"). Default = "/"
 *  - noindex: daca paginas e protejata cu auth sau nu vrem sa fie indexata
 *  - ogImage: URL absolut la imagine. Default = logo.jpg
 *  - jsonLd: array de obiecte Schema.org care se serializeaza ca JSON-LD
 */
export default function SEO({
  title,
  description,
  path = '/',
  noindex = false,
  ogImage = DEFAULT_OG_IMAGE,
  jsonLd = [],
}) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'ro').startsWith('en') ? 'en' : 'ro';
  const ogLocale = lang === 'en' ? 'en_US' : 'ro_RO';
  const htmlLang = lang === 'en' ? 'en-US' : 'ro-RO';

  // Canonical-ul pointeaza MEREU la reprezentantul RO (URL curat, fara ?lang).
  // EN e declarat doar prin hreflang alternate. Astfel DOM-ul randat client-side
  // ramane consistent cu HTML-ul static prerendat (ambele = URL RO curat) si nu
  // mai produce "Dublura fara pagina canonica selectata" in GSC: variantele
  // ?lang=en se consolideaza corect pe pagina RO. (NU face canonical sa urmeze
  // limba activa — self-canonical pe ?lang=en sparge consolidarea.)
  const fullUrlRO = `${SITE_URL}${path}`;
  const sep = path.includes('?') ? '&' : '?';
  const fullUrlEN = `${SITE_URL}${path}${sep}lang=en`;
  const canonicalUrl = fullUrlRO;
  const ogUrl = lang === 'en' ? fullUrlEN : fullUrlRO;
  const fullTitle = title.includes('Private Brands') ? title : `${title} | Private Brands`;

  return (
    <Helmet>
      <html lang={htmlLang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang — fiecare limba pointeaza la propriul URL (RO = path, EN = path?lang=en) */}
      <link rel="alternate" hrefLang="ro-RO" href={fullUrlRO} />
      <link rel="alternate" hrefLang="en" href={fullUrlEN} />
      <link rel="alternate" hrefLang="x-default" href={fullUrlRO} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:site_name" content="Private Brands" />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Schema.org — Organization si WebSite globale + scheme custom per pagina */}
      {!noindex && (
        <script type="application/ld+json">
          {JSON.stringify(ORGANIZATION_SCHEMA)}
        </script>
      )}
      {!noindex && (
        <script type="application/ld+json">
          {JSON.stringify(WEBSITE_SCHEMA)}
        </script>
      )}
      {jsonLd.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
